/**
 * The Planner's Overdue block: a one-line header (● N overdue since …) that
 * folds its rows, closed by default (spec 2026-10-14 §8). The rows are the
 * incomplete past-due todos, oldest first. The screen owns the filter/sort;
 * this component presents the list and owns how rows LEAVE it — and how they
 * come back. Ticking a row commits the toggle at once (the store drops the
 * todo optimistically), but the row does not blink out with it: a snapshot
 * stays in its slot showing the checkmark, holds a beat, then collapses so the
 * rows below glide up. Once the last row has gone the block collapses the same
 * way and only then unmounts — the screen never jumps. The way back mirrors
 * the way out: a todo that becomes overdue again (an un-check from the day
 * list, or a toggle that rolled back) grows into its slot, and a block that
 * had gone opens the way it closed. The block also owns the gaps around it
 * (`gapAbove`, `gapBelow`) for that reason: they have to go — and come — WITH
 * the block, not a frame later when the parent's flex gap notices.
 *
 * The header folds the rows the same way: they shrink away and unmount, or
 * mount clipped and grow back, while the chevron turns rather than swapping
 * glyphs. The block and the rows each fold with a `Fold`.
 *
 * Always mounted by the screen; renders nothing when it has nothing to show.
 * Reduced motion keeps the instant behavior.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  View,
  StyleSheet,
  type LayoutChangeEvent,
} from 'react-native';
import { Text } from '@/components/common/text';
import Icon from 'react-native-vector-icons/Ionicons';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import { useMotion } from '@/hooks/shared/useMotion';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';
import { useLastDefined } from '@/hooks/common/useLastDefined';
import { overdueShortLabel, overdueSince } from '@/utils/residents/todoCalendar';
import type { ResidentTodo } from '@/types/residents/todos';
import { Fold, foldLayoutStyles } from './Fold';
import { CHECKBOX, CIRCLE_REACH, TodoRow } from './TodoRow';

interface OverdueSectionProps {
  /** Already filtered (incomplete, past-due) and sorted oldest-first. */
  todos: ResidentTodo[];
  /** Today: the line's "since …" and the rows' short dates count back from it. */
  todayKey: string;
  onToggle: (todo: ResidentTodo) => void;
  onEdit: (todo: ResidentTodo) => void;
  /** Open on mount. The Planner starts closed (spec §8); the row-machinery tests start open. */
  initiallyExpanded?: boolean;
  /** Space above the line and below the block. Inside the collapse, so it leaves with it. */
  gapAbove?: number;
  gapBelow?: number;
}

export interface DisplayRow {
  todo: ResidentTodo;
  /** Gone from `todos`; kept only until its exit animation finishes. */
  leaving: boolean;
  /** Not among the previous rows; grows into its slot on mount. */
  entering: boolean;
}

/**
 * Rows for the next render: `todos` in its own order plus — when keeping
 * them — any row that just vanished, left in its old slot and flagged so it
 * can animate out in place. A flagged row that is back in `todos` is simply a
 * present row again (fresh object, flag cleared). With `flagNew`, a todo that
 * was not among the previous rows is marked entering so it can grow in.
 */
export const reconcileRows = (
  prev: DisplayRow[],
  todos: ResidentTodo[],
  keepVanished: boolean,
  flagNew = false,
): DisplayRow[] => {
  const prevIds = new Set(prev.map(r => r.todo.id));
  const next: DisplayRow[] = todos.map(todo => ({
    todo,
    leaving: false,
    entering: flagNew && !prevIds.has(todo.id),
  }));
  if (!keepVanished) return next;
  const present = new Set(todos.map(t => t.id));
  prev.forEach((row, index) => {
    if (present.has(row.todo.id)) return;
    next.splice(Math.min(index, next.length), 0, {
      todo: row.todo,
      leaving: true,
      entering: false,
    });
  });
  return next;
};

/** `ids` minus `drop`; the same instance when nothing changes, so a
 *  render-phase setState with the result bails out. */
const without = (
  ids: ReadonlySet<string>,
  drop: Iterable<string>,
): ReadonlySet<string> => {
  let next: Set<string> | null = null;
  for (const id of drop) {
    if (!ids.has(id)) continue;
    next ??= new Set(ids);
    next.delete(id);
  }
  return next ?? ids;
};

/**
 * How far a leaving row drifts up while it fades, and how far below its slot
 * an arriving row settles up from (AnimatedEntrance's 8).
 */
const EXIT_RISE = 8;

/** The red dot's diameter, in the circle column of the line. */
const OVERDUE_DOT = 7;

/**
 * A row's targets reach CIRCLE_REACH into the gutter each side of it (the toggle's left, the pencil's
 * right), and its animated wrapper clips (overflow:hidden) while the row leaves or arrives. So the wrapper
 * reaches that far a side and the measured view inside pads back: the targets and the hovered row's plate
 * stay whole, and the row stays where it was. The padding sits on the measured view, not the wrapper, so
 * the row lines up the same out of flow (an absolute child ignores its parent's padding). You tick a row
 * with the pointer on it. The same gutter the home list uses.
 */
const rowStyles = StyleSheet.create({
  gutter: { marginHorizontal: -CIRCLE_REACH },
  padBack: { paddingHorizontal: CIRCLE_REACH },
});

interface OverdueRowProps {
  todo: ResidentTodo;
  /** Today: the row's short date counts back from it. */
  todayKey: string;
  /** Ticked here: draw the checkmark and hold a beat before collapsing. */
  checked: boolean;
  leaving: boolean;
  /** Mounted for a todo that just became overdue again: grow in. */
  entering: boolean;
  onToggle: (todo: ResidentTodo) => void;
  onEdit: (todo: ResidentTodo) => void;
  onExited: (id: string) => void;
}

/**
 * One row plus its exit and its entrance. The outer view is what shrinks or
 * grows (height is a layout prop, so this runs on the JS driver — fine for a
 * handful of rows, and it is what makes the rows below move with it); the
 * inner view keeps the natural height and is where that height gets measured —
 * out of flow while the outer view is pinned, as in Fold (foldLayoutStyles.outOfFlow).
 * An arriving row is clipped to nothing until that first measurement lands.
 */
const OverdueRow: React.FC<OverdueRowProps> = ({
  todo,
  todayKey,
  checked,
  leaving,
  entering,
  onToggle,
  onEdit,
  onExited,
}) => {
  const motion = useMotion();
  // Decided at mount: a row only ever grows in from nothing.
  const growIn = useRef(entering).current;
  const progress = useRef(new Animated.Value(growIn ? 0 : 1)).current;
  const heightRef = useRef<number | null>(null);
  // The height the outer view is animating over; null = natural layout.
  const [animHeight, setAnimHeight] = useState<number | null>(null);
  // False until an arriving row has grown to full size.
  const [landed, setLanded] = useState(!growIn);
  const wasLeaving = useRef(false);

  const handleLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const { height } = e.nativeEvent.layout;
      heightRef.current = height;
      // An arriving row can grow once it knows how tall it is.
      if (!landed && animHeight === null) setAnimHeight(height);
    },
    [landed, animHeight],
  );

  // Exit: pin the outer view to the natural height and shrink it away. Only
  // the flip to `leaving` starts one, so a re-render mid-exit can't restart it.
  useEffect(() => {
    const justLeft = leaving && !wasLeaving.current;
    wasLeaving.current = leaving;
    if (!justLeft) return;
    setAnimHeight(heightRef.current);
    const exit = Animated.sequence([
      // A tapped row shows its checkmark for a beat before it goes; a row that
      // left for another reason (edited to a later date) just goes.
      Animated.delay(checked ? motion.duration.base : 0),
      Animated.timing(progress, {
        toValue: 0,
        duration: motion.duration.base,
        easing: Easing.bezier(...motion.easing.standard),
        useNativeDriver: false,
      }),
    ]);
    exit.start(({ finished }) => {
      if (finished) onExited(todo.id);
    });
    return () => exit.stop();
    // `checked` is read as the exit starts; a later change must not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaving, motion, progress, onExited, todo.id]);

  // Settle: a pinned row that is not leaving grows to full size — an arrival
  // once measured, or a rolled-back toggle turning its collapse around — then
  // hands its slot back to natural layout.
  useEffect(() => {
    if (leaving || animHeight === null) return;
    const settle = Animated.timing(progress, {
      toValue: 1,
      duration: motion.duration.base,
      easing: Easing.bezier(...motion.easing.standard),
      useNativeDriver: false,
    });
    settle.start(({ finished }) => {
      if (!finished) return;
      setAnimHeight(null);
      setLanded(true);
    });
    return () => settle.stop();
  }, [leaving, animHeight, motion, progress]);

  // The store has already dropped a ticked todo, so the snapshot has to be
  // told to draw the checkmark; a row that is present always shows live state.
  const shownTodo = leaving && checked ? { ...todo, completed: true } : todo;
  // A row on its way out drifts up; one on its way in settles up from below.
  const yFrom = landed ? -EXIT_RISE : EXIT_RISE;
  const animStyle =
    animHeight !== null
      ? [
          foldLayoutStyles.clip,
          {
            height: progress.interpolate({ inputRange: [0, 1], outputRange: [0, animHeight] }),
            opacity: progress,
            transform: [
              {
                translateY: progress.interpolate({
                  inputRange: [0, 1],
                  outputRange: [yFrom, 0],
                }),
              },
            ],
          },
        ]
      : landed
        ? undefined
        : [foldLayoutStyles.clip, foldLayoutStyles.unmeasured];

  // Pinned (0 until measured, or the animated height): measure out of flow.
  const pinned = animHeight !== null || !landed;

  return (
    <Animated.View style={[rowStyles.gutter, animStyle]}>
      <View onLayout={handleLayout} style={[rowStyles.padBack, pinned && foldLayoutStyles.outOfFlow]}>
        <TodoRow
          todo={shownTodo}
          onToggle={onToggle}
          onEdit={onEdit}
          dateLabel={overdueShortLabel(todo, todayKey)}
        />
      </View>
    </Animated.View>
  );
};

export const OverdueSection: React.FC<OverdueSectionProps> = ({
  todos,
  todayKey,
  onToggle,
  onEdit,
  initiallyExpanded = false,
  gapAbove = 0,
  gapBelow = 0,
}) => {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const type = useTypography();
  const space = useSpacing();
  const radius = useRadius();
  const motion = useMotion();
  const reduceMotion = useReducedMotion();
  const animate = !reduceMotion;

  // Rows lag `todos` so a vanished row can finish leaving. Synced during
  // render, not in an effect, so the frame that drops a todo is the frame
  // that flags its row — no in-between commit.
  const [syncedTodos, setSyncedTodos] = useState(todos);
  const [rows, setRows] = useState<DisplayRow[]>(() => reconcileRows([], todos, false));
  // Ids ticked here, so their snapshot rows know to draw the checkmark.
  const [checkedIds, setCheckedIds] = useState<ReadonlySet<string>>(() => new Set());
  if (syncedTodos !== todos) {
    setSyncedTodos(todos);
    // Rows behind the fold can't animate, so they just go — or just appear.
    // A newcomer grows in on its own only when it joins rows that are
    // already up; a block that is opening (or turning around from a close,
    // with only leaving rows left) carries it in with its own growth.
    const live = animate && expanded;
    setRows(reconcileRows(rows, todos, live, live && rows.some(r => !r.leaving)));
    // A ticked todo that is back in the list means its toggle rolled back.
    setCheckedIds(without(checkedIds, todos.map(t => t.id)));
  }
  const hasRows = rows.length > 0;

  // The chevron turns with the fold: down while expanded, right while folded.
  // JS-driven like the fold itself, so the two stay in step. Only a flip of
  // `expanded` turns it: a timing to the value already held would still run
  // its full duration, leaking a frame loop from every mount for nothing.
  const chevron = useRef(new Animated.Value(expanded ? 1 : 0)).current;
  const wasExpanded = useRef(expanded);
  useEffect(() => {
    const target = expanded ? 1 : 0;
    const flipped = wasExpanded.current !== expanded;
    wasExpanded.current = expanded;
    if (!animate || !flipped) {
      chevron.setValue(target);
      return;
    }
    const turn = Animated.timing(chevron, {
      toValue: target,
      duration: motion.duration.base,
      easing: Easing.bezier(...motion.easing.standard),
      useNativeDriver: false,
    });
    turn.start();
    return () => turn.stop();
  }, [expanded, animate, chevron, motion]);

  const handleToggle = useCallback(
    (todo: ResidentTodo) => {
      if (animate) setCheckedIds(prev => new Set(prev).add(todo.id));
      onToggle(todo);
    },
    [animate, onToggle],
  );
  const handleRowExited = useCallback((id: string) => {
    setRows(prev => prev.filter(r => r.todo.id !== id));
    setCheckedIds(prev => without(prev, [id]));
  }, []);
  const handleHeaderPress = useCallback(() => {
    setExpanded(e => !e);
    // Rows behind the fold can't finish an exit, or an entrance: drop any exit
    // in flight, and clear entering flags so a row comes back whole on unfold
    // (a row reads its flag once, at mount, so a landed one is cleared here too).
    const inFlight = rows.filter(r => r.leaving);
    if (inFlight.length > 0 || rows.some(r => r.entering)) {
      setRows(
        rows.filter(r => !r.leaving).map(r => (r.entering ? { ...r, entering: false } : r)),
      );
      setCheckedIds(prev => without(prev, inFlight.map(r => r.todo.id)));
    }
  }, [rows]);

  const styles = useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.md,
        minHeight: 44,
      },
      dotSlot: {
        width: CHECKBOX,
        alignItems: 'center',
      },
      dot: {
        width: OVERDUE_DOT,
        height: OVERDUE_DOT,
        borderRadius: radius.full,
        backgroundColor: semantic.status.errorText,
      },
      headerText: {
        ...type.subhead,
        color: colors.text.secondary,
        flex: 1,
      },
      chevron: {
        color: colors.text.quiet,
      },
    }),
  );

  // Remaining work while there is any; otherwise whatever is still on its way
  // out. Never "0 overdue": by then the block is closing, not showing an empty list.
  const shownCount = todos.length || rows.length || 1;
  // From the oldest REMAINING to-do (todos is oldest-first), not a row on its way out. Once nothing is
  // left it holds the last one read, so the line keeps its "since …" while the block closes.
  const oldestKey = useLastDefined(todos[0]?.dueDate);
  const headerLabel = `${shownCount} overdue ${oldestKey ? overdueSince(oldestKey, todayKey) : ''}`.trim();
  const gapStyle =
    gapAbove > 0 || gapBelow > 0 ? { paddingTop: gapAbove, paddingBottom: gapBelow } : undefined;

  return (
    <Fold open={hasRows} animate={animate} innerStyle={gapStyle}>
      <Hoverable
        hover="row"
        bleed={space.sm}
        style={styles.headerRow}
        onPress={handleHeaderPress}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={headerLabel}
      >
        <View style={styles.dotSlot}>
          <View style={styles.dot} />
        </View>
        <Text style={styles.headerText}>{headerLabel}</Text>
        <Animated.View
          style={{
            transform: [
              {
                rotate: chevron.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['-90deg', '0deg'],
                }),
              },
            ],
          }}
        >
          <Icon name="chevron-down" size={16} style={styles.chevron} />
        </Animated.View>
      </Hoverable>
      <Fold open={expanded} animate={animate}>
        <View>
          {rows.map(row => (
            <OverdueRow
              key={row.todo.id}
              todo={row.todo}
              todayKey={todayKey}
              checked={checkedIds.has(row.todo.id)}
              leaving={row.leaving}
              entering={row.entering}
              onToggle={handleToggle}
              onEdit={onEdit}
              onExited={handleRowExited}
            />
          ))}
        </View>
      </Fold>
    </Fold>
  );
};

OverdueSection.displayName = 'OverdueSection';
